import React from 'react';
import { motion } from 'framer-motion';
import combos from '../data/combos';
import ComboCard from '../components/ComboCard';

// Importa la imagen de referencia proporcionada por el usuario.
import sucarneImg from '../assets/background.png';
import logo from '../assets/logo.jpg';

/**
 * Página principal del sitio con animaciones mejoradas.
 * Contiene un héroe destacado, la lista de combos y la sección de la Parrilla Box.
 */
export default function Home() {
  return (
    <main>
      {/* Sección héroe con imagen de fondo */}
      <section
        className="relative h-[75vh] md:h-[85vh] flex items-center justify-center text-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)), url(${sucarneImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 px-4 max-w-4xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6, type: 'spring' }}
            className="mb-6"
          >
            <img
              src={logo}
              alt="Su Carne"
              className="h-28 w-28 md:h-36 md:w-36 mx-auto rounded-full shadow-2xl border-4 border-white"
            />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight"
          >
            🥩 Su Carne 🥩
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-3xl mb-4 font-bold text-secondary"
          >
            La mejor carne de Mercedes
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="text-lg md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Calidad premium • Cortes selectos • Entrega a domicilio<br />
            <span className="text-secondary font-semibold">¡Armá tu parrilla perfecta y recibila en tu casa!</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.a
              whileHover={{ scale: 1.08, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
              whileTap={{ scale: 0.95 }}
              href="#combos"
              className="inline-block bg-primary px-8 py-4 rounded-full text-white text-lg font-bold hover:bg-primary-dark transition-all shadow-2xl"
            >
              🛒 Ver Combos
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.08, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
              whileTap={{ scale: 0.95 }}
              href="https://wa.me/59892696366"
              className="inline-block bg-green-500 px-8 py-4 rounded-full text-white text-lg font-bold hover:bg-green-600 transition-all shadow-2xl"
            >
              📱 Contactar por WhatsApp
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Sección de beneficios */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
          >
            <motion.div
              whileHover={{ y: -10, scale: 1.05 }}
              className="p-6 bg-white rounded-xl shadow-lg"
            >
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-2xl font-bold text-primary mb-3">Calidad Premium</h3>
              <p className="text-gray-600">Cortes seleccionados de la mejor carne para tu parrilla</p>
            </motion.div>

            <motion.div
              whileHover={{ y: -10, scale: 1.05 }}
              className="p-6 bg-white rounded-xl shadow-lg"
            >
              <div className="text-6xl mb-4">🚚</div>
              <h3 className="text-2xl font-bold text-primary mb-3">Envío a Domicilio</h3>
              <p className="text-gray-600">Recibí tu pedido en la puerta de tu casa</p>
            </motion.div>

            <motion.div
              whileHover={{ y: -10, scale: 1.05 }}
              className="p-6 bg-white rounded-xl shadow-lg"
            >
              <div className="text-6xl mb-4">💰</div>
              <h3 className="text-2xl font-bold text-primary mb-3">Mejores Precios</h3>
              <p className="text-gray-600">Combos diseñados para que ahorres y disfrutes más</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Sección de combos */}
      <section id="combos" className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-primary">
            🔥 Nuestros Combos Parrilleros 🔥
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Elegí el combo perfecto para tu reunión. ¡Todo listo para la parrilla!
          </p>
        </motion.div>

        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
          {combos.map((combo, index) => (
            <ComboCard key={combo.id} combo={combo} index={index} />
          ))}
        </div>
      </section>

      {/* Sección de Parrilla Box */}
      <section id="box" className="bg-gradient-to-r from-primary to-red-700 text-white py-16 -mb-12">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center md:space-x-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:w-1/2 mb-8 md:mb-0"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">🎁 Parrilla Box</h2>
            <p className="text-xl leading-relaxed mb-6">
              <strong>La nueva forma de disfrutar de la parrilla en casa.</strong> Nuestro Pack Parrilla
              Box incluye todos los cortes que buscás: carnes seleccionadas, chorizos y
              achuras, listos para que sólo prendas el fuego.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
              <ul className="space-y-3 text-lg">
                <li className="flex items-center gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Carnes premium seleccionadas</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Chorizos artesanales</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Achuras frescas</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Envío a todo Mercedes</span>
                </li>
              </ul>
            </div>
            <motion.a
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
              whileTap={{ scale: 0.95 }}
              href="#contacto"
              className="inline-block bg-secondary text-primary px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl"
            >
              🔥 Consultar ahora
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:w-1/2 flex justify-center"
          >
            <motion.img
              whileHover={{ scale: 1.05, rotate: 3 }}
              transition={{ duration: 0.3 }}
              src={sucarneImg}
              alt="Parrilla Box"
              className="rounded-2xl shadow-2xl w-full max-w-md object-cover border-8 border-white/20"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}